/**
 * Справочник валют — общий для сервера и клиента.
 *
 * Правило хранения денег во всех модулях: сумма — ЦЕЛОЕ число в МИНОРНЫХ
 * единицах (копейки, центы, сатанги) плюс ISO-код валюты в самой записи.
 * Дробных сумм в базе нет: 990 ₽ хранится как { amount: 99000, currency: 'RUB' }.
 *
 * Сколько минорных единиц в мажорной — свойство ВАЛЮТЫ (exponent), а не записи.
 * Поэтому поля вида «в чём хранится цена этого варианта» не существует и заводить
 * его нельзя: у RUB и USD exponent 2, у JPY — 0, и это всё, что нужно знать.
 *
 * Файл без mongoose и без vue: его импортируют и модели, и компоненты.
 */

export const CURRENCIES = {
  RUB: { code: 'RUB', symbol: '₽', exponent: 2, locale: 'ru-RU' },
  USD: { code: 'USD', symbol: '$', exponent: 2, locale: 'en-US' },
  EUR: { code: 'EUR', symbol: '€', exponent: 2, locale: 'de-DE' },
  GBP: { code: 'GBP', symbol: '£', exponent: 2, locale: 'en-GB' },
  THB: { code: 'THB', symbol: '฿', exponent: 2, locale: 'th-TH' },
  GEL: { code: 'GEL', symbol: '₾', exponent: 2, locale: 'ka-GE' },
  KZT: { code: 'KZT', symbol: '₸', exponent: 2, locale: 'ru-KZ' },
  IDR: { code: 'IDR', symbol: 'Rp', exponent: 2, locale: 'id-ID' },
  INR: { code: 'INR', symbol: '₹', exponent: 2, locale: 'en-IN' },
  JPY: { code: 'JPY', symbol: '¥', exponent: 0, locale: 'ja-JP' },
};

export const CURRENCY_CODES = Object.keys(CURRENCIES);

// Валюта по умолчанию, если инстанс не объявил ни одной.
export const DEFAULT_CURRENCY = 'USD';

// Валюта страны: ISO-2 страны аккаунта → ISO-код валюты. Страны, которой нет
// в таблице, платят в DEFAULT_CURRENCY — это не пробел, а правило: свою
// валюту получает страна, для которой у инстанса есть и цены, и платёжный
// провайдер. Строка сюда добавляется вместе с ними.
// Страну берём у АККАУНТА, а не у IP: за VPN и в поездке IP чужой, а списание
// уходит настоящее.
export const country_currency = {
  ru: 'RUB',
};

// Страна аккаунта → валюта. Регистр страны не важен: ISO-2 хранится в верхнем,
// а приходит по-разному.
export function currencyForCountry(country) {
  return country_currency[String(country || '').toLowerCase()] || DEFAULT_CURRENCY;
}

const CODE_BY_SYMBOL = Object.values(CURRENCIES).reduce((map, currency) => {
  if (!(currency.symbol in map)) map[currency.symbol] = currency.code;
  return map;
}, {});

/**
 * Приводит что угодно к ISO-коду валюты. Понимает и код ('RUB', 'rub'), и символ
 * ('₽') — символ приходит из старых настроек инстанса (options.currency).
 */
export function resolveCurrencyCode(value, fallback = DEFAULT_CURRENCY) {
  if (!value) return fallback;

  const raw = String(value).trim();
  const upper = raw.toUpperCase();

  if (CURRENCIES[upper]) return upper;
  if (CODE_BY_SYMBOL[raw]) return CODE_BY_SYMBOL[raw];

  return fallback;
}

export function getCurrency(code) {
  return CURRENCIES[resolveCurrencyCode(code)];
}

/** Во сколько раз мажорная единица больше минорной: 100 для RUB, 1 для JPY. */
export function getCurrencyFactor(code) {
  return 10 ** getCurrency(code).exponent;
}

/** Мажорные единицы (то, что вводит человек) → минорные (то, что лежит в базе). */
export function toMinor(major, code) {
  const value = typeof major === 'string' ? parseFloat(major.replace(',', '.')) : major;
  if (value == null || Number.isNaN(value)) return 0;
  return Math.round(value * getCurrencyFactor(code));
}

/** Минорные единицы → мажорные. Дробный результат — это нормально. */
export function fromMinor(minor, code) {
  if (minor == null || Number.isNaN(Number(minor))) return 0;
  return Number(minor) / getCurrencyFactor(code);
}

export function isMoney(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value) && typeof value.amount === 'number' && typeof value.currency === 'string';
}

export function createMoney(amount, code) {
  return { amount: Math.round(Number(amount) || 0), currency: resolveCurrencyCode(code) };
}

/**
 * Достаёт из массива цен цену ровно запрошенной валюты. Нет такой — null.
 *
 * Фолбэка на «первую попавшуюся» здесь нет намеренно: подставленная чужая
 * валюта молча превращается в списание не в той валюте. Что делать с null —
 * отказать в покупке или убрать позицию из витрины — решает вызывающий.
 *
 * ГДЕ ЭТО ВЫЗЫВАТЬ. Форма ответа зависит от аудитории эндпоинта:
 *   - бекофис (/api/variants, CRM клиента) отдаёт prices целиком — там цены
 *     редактируют и выбирают под валюту заказа. pickPrice зовёт потребитель;
 *   - витрина и кабинет (/api/products, аренда, тарифы, /api/subscriptions)
 *     отдают уже свёрнутую price. pickPrice зовёт сервер, на фронт массив
 *     не уходит вообще.
 * Единственное место на фронте, которому нужен массив, — форма редактирования
 * варианта.
 */
export function pickPrice(prices, code) {
  const wanted = CURRENCIES[String(code || '').toUpperCase()] ? String(code).toUpperCase() : null;
  if (!wanted) return null;

  if (isMoney(prices)) return prices.currency === wanted ? prices : null;
  if (!Array.isArray(prices)) return null;

  return prices.find(price => isMoney(price) && price.currency === wanted) || null;
}

/**
 * Money → строка для показа. Символ, разделители разрядов и позиция символа —
 * из Intl по локали валюты: «1 490 ₽», «$12.50», «¥1,200».
 * Целые суммы показываем без копеек — иначе прайс-лист превращается в «990,00 ₽».
 */
export function formatMoney(money, options = {}) {
  const value = pickPrice(money, options.currency);
  if (!isMoney(value)) return '';

  const currency = getCurrency(value.currency);
  const factor = getCurrencyFactor(currency.code);
  const digits = value.amount % factor === 0 ? 0 : currency.exponent;

  return new Intl.NumberFormat(options.locale || currency.locale, {
    style: 'currency',
    currency: currency.code,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value.amount / factor);
}

export default {
  CURRENCIES,
  CURRENCY_CODES,
  DEFAULT_CURRENCY,
  country_currency,
  currencyForCountry,
  resolveCurrencyCode,
  getCurrency,
  getCurrencyFactor,
  toMinor,
  fromMinor,
  isMoney,
  createMoney,
  pickPrice,
  formatMoney,
};
